import { AddIcon } from '@ordr-as/icons'
import { Banner, Button, LoadingWand, Switch } from '@ordr-as/umami-react'
import AddFavritAppToCountryMenu from 'components/AddFavritAppToCountryMenu'
import Chip from 'components/Chip'
import ChipGroup from 'components/Chip/ChipGroup'
import DetailTemplate from 'components/DetailTemplate'
import RemoveAppFromCountryDialog from 'components/RemoveAppFromCountryDialog'
import { useCreatePaymentMethodCountryConnection, usePaymentMethodCountryConnections } from 'hooks/usePaymentMethodApi'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import ReactTooltip from 'react-tooltip'
import { mapCountryCodeToCountryName } from 'utils/countryUtils'
import { formatDate } from 'utils/dateUtils'
import { mapFavritAppToName } from 'utils/favritAppUtils'
import { CountryCode, FavritApp, PaymentProvider } from '../../../generated-api'
import { compareDesc } from 'date-fns'

type PaymentMethodCountryConnectionDetailsProps = {
    onClose: () => void
}

const PaymentMethodCountryConnectionDetails = (props: PaymentMethodCountryConnectionDetailsProps) => {
    const { onClose } = props
    const { paymentProvider, paymentMethod, countryCode } = useParams()
    const [showChangeHistory, setShowChangeHistory] = useState(false)

    if (!paymentProvider || !paymentMethod || !countryCode) {
        throw new Error('Payment provider, payment method and country code must be present in path.')
    }

    const [favritAppToRemove, setFavritAppToRemove] = useState<FavritApp | undefined>(undefined)

    const {
        data: countryConnections,
        isLoading,
        isSuccess,
        isError,
        error,
    } = usePaymentMethodCountryConnections(
        paymentProvider as PaymentProvider,
        paymentMethod as string,
        countryCode as CountryCode
    )

    const { mutate: runCreateCountryConnection, isLoading: isCreatingCountryConnection } =
        useCreatePaymentMethodCountryConnection(
            paymentProvider as PaymentProvider,
            paymentMethod,
            countryCode as CountryCode
        )

    const activeConnection = countryConnections?.find((cc) => cc.isActive)

    const sortedCountryConnections = [...(countryConnections || [])].sort((a, b) =>
        compareDesc(a.activeFrom, b.activeFrom)
    )

    const onTogglePaymentsEnabled = (value: boolean) => {
        runCreateCountryConnection({
            paymentsEnabled: value,
            defaultEnabledInApps: activeConnection!.defaultEnabledInApps,
        })
    }

    return (
        <DetailTemplate
            heading={`${mapCountryCodeToCountryName(countryCode as CountryCode)} (${countryCode})`}
            closeButtonTooltip="Close country settings"
            onClose={onClose}
        >
            {isLoading && <LoadingWand className="mx-auto" />}

            {isError && (
                <Banner kind="error" onClose={() => null} stretch="parent" className="my-3">
                    {(error as Error).message}
                </Banner>
            )}

            {isSuccess && (
                <div>
                    {!activeConnection && (
                        <Banner className="mb-5" kind="warn" stretch="parent" onClose={() => null}>
                            This country has no active configuration for the payment method
                        </Banner>
                    )}
                    {activeConnection && (
                        <div>
                            <div className="mb-5">
                                <h4 className="mb-1">
                                    Payments are {activeConnection.paymentsEnabled ? 'enabled' : 'disabled'}
                                </h4>
                                <Switch
                                    isSelected={activeConnection.paymentsEnabled}
                                    onChange={onTogglePaymentsEnabled}
                                    isDisabled={isCreatingCountryConnection}
                                    className="m-0"
                                    aria-label="Toggle payments enabled"
                                />
                            </div>
                            <div className="mb-5">
                                <h4 className="mb-1">Enabled in</h4>
                                <div className="flex">
                                    {Array.from(activeConnection.defaultEnabledInApps).map((app) => (
                                        <Chip
                                            key={`${activeConnection.countryCode}-${app}`}
                                            size="medium"
                                            text={mapFavritAppToName(app)}
                                            closeButtonTooltip="Remove Favrit App"
                                            onClose={() => setFavritAppToRemove(app)}
                                        />
                                    ))}
                                    {activeConnection.defaultEnabledInApps.size === 0 && (
                                        <div className="flex flex-col justify-center text-sm text-grey-dark mr-2">
                                            <div>None</div>
                                        </div>
                                    )}
                                    {activeConnection.defaultEnabledInApps.size < Object.values(FavritApp).length && (
                                        <AddFavritAppToCountryMenu
                                            countryConnection={activeConnection}
                                            buttonElement={
                                                <div
                                                    data-tip
                                                    data-for="add-favrit-app-to-country-button-tooltip"
                                                    className="flex flex-col justify-center items-center w-[30px] min-h-[28px] rounded cursor-pointer hover:bg-purple-lightest"
                                                >
                                                    <AddIcon height={17} width={17} />
                                                </div>
                                            }
                                        />
                                    )}

                                    <ReactTooltip
                                        id="add-favrit-app-to-country-button-tooltip"
                                        arrowColor="transparent"
                                        effect="solid"
                                        padding="1px 4px"
                                        className="text-bold !opacity-100"
                                    >
                                        Add Favrit App
                                    </ReactTooltip>
                                </div>
                            </div>

                            {favritAppToRemove && (
                                <RemoveAppFromCountryDialog
                                    countryConnection={activeConnection}
                                    favritApp={favritAppToRemove}
                                    dialogIsOpen={favritAppToRemove !== undefined}
                                    onClose={() => setFavritAppToRemove(undefined)}
                                />
                            )}
                        </div>
                    )}

                    <div>
                        <h4 className="mb-1">Change history</h4>

                        {
                            <Button
                                kind="secondary"
                                size="small"
                                onClick={() => setShowChangeHistory(!showChangeHistory)}
                            >
                                {showChangeHistory ? 'Hide changes' : 'Show changes'}
                            </Button>
                        }

                        <div className="divide-y divide-[#e2e8f0]">
                            {showChangeHistory &&
                                sortedCountryConnections?.map((cc) => (
                                    <div key={cc.externalId} className="flex flex-col pb-4 pt-4">
                                        <div className="flex mb-3">
                                            <div className="flex flex-col mr-5">
                                                <div className="text-xs text-grey-dark mb-1">Payments was</div>

                                                <div
                                                    className={`text-bold ${
                                                        cc.paymentsEnabled ? 'text-green-dark' : 'text-orange'
                                                    }`}
                                                >
                                                    {cc.paymentsEnabled ? 'Enabled' : 'Disabled'}
                                                </div>
                                            </div>
                                            <ChipGroup
                                                heading="Enabled in"
                                                showEmptyPlaceholder={cc.defaultEnabledInApps.size === 0}
                                            >
                                                {Array.from(cc.defaultEnabledInApps).map((app) => (
                                                    <Chip
                                                        key={`${cc.countryCode}-${app}`}
                                                        size="small"
                                                        text={mapFavritAppToName(app)}
                                                    />
                                                ))}
                                            </ChipGroup>
                                        </div>

                                        <div className="text-sm mb-1">
                                            Config active from {formatDate(cc.activeFrom)} to{' '}
                                            {cc.activeTo ? formatDate(cc.activeTo) : 'eternity'}
                                        </div>
                                        <div className="text-xs text-grey-dark mb-1">
                                            Config created by {cc.createdByUserEmail} at {formatDate(cc.created)}
                                        </div>
                                        <div className="text-xs text-grey-dark mb-1">
                                            Config updated by {cc.updatedByUserEmail} at {formatDate(cc.updated)}{' '}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            )}
        </DetailTemplate>
    )
}

export default PaymentMethodCountryConnectionDetails
